import { cn, isLinkActive } from '@sohanemon/utils';
import { Iconify, Portal } from '@sohanemon/utils/components';
import { m } from '@sohanscript/i18n';
import { formatNumber } from '@sohanscript/lib/utils/format';
import { ClickMock } from '@sohanscript/theme/ui';
import { Heading } from '@sohanscript/ui/heading';
import { Icons } from '@sohanscript/ui/icons';
import { Motion } from '@sohanscript/ui/motion';
import { SrOnly } from '@sohanscript/ui/sr-only';
import { P } from '@sohanscript/ui/text';
import { Link, useLocation } from '@tanstack/react-router';
import { type ComponentProps, useLayoutEffect, useRef, useState } from 'react';
import { useSohanEmon } from '#/lib/hooks/sohanemon.tsx';
import { useClickMockStore } from '#/lib/store/click-mock.ts';
import { getAppEnv } from '#/lib/utils/app-env.ts';
import { Button } from '@/components/ui/button';

type AboutProps = ComponentProps<'div'> & { delay?: number };

const noContact = getAppEnv('no-contact');
const env = getAppEnv();

const isNonePlatform = env !== 'fiverr' && env !== 'upwork';

function BadgePill({
	variant,
	className,
	...props
}: ComponentProps<'span'> & { variant: 'fiverr' | 'upwork' }) {
	if (variant === 'fiverr') {
		return (
			<span
				className={cn(
					'inline-flex items-center gap-3 rounded-full border border-primary/10 bg-primary/5 px-4 py-1.5 backdrop-blur-sm transition-colors hover:border-primary/20',
					className,
				)}
				{...props}
			>
				<span className="flex items-center gap-0.5 text-primary">
					<Icons.star className="size-3.5" />
					<Icons.star className="size-3.5" />
					<Icons.star className="size-3.5" />
					<Icons.star className="size-3.5" />
					<Icons.star className="size-3.5" />
				</span>
				<span className="h-4 w-px bg-primary/10" />
				<span className="font-mono text-primary text-sm tabular-nums">
					{formatNumber(5, {
						minimumFractionDigits: 1,
						maximumFractionDigits: 1,
					})}
				</span>
				<span className="text-muted-foreground text-xs">·</span>
				<span className="font-mono text-muted-foreground text-sm tabular-nums">
					{m.about_clients({ count: formatNumber(63) })}
				</span>
			</span>
		);
	}

	return (
		<span
			className={cn(
				'inline-flex items-center gap-3 rounded-full border border-primary/10 bg-primary/5 px-4 py-1.5 backdrop-blur-sm transition-colors hover:border-primary/20',
				isNonePlatform && 'border-transparent bg-transparent',
				className,
			)}
			{...props}
		>
			<Iconify icon="lucide:circle-check" className="size-4 text-success" />
			<span className="font-mono font-semibold text-sm text-success tracking-tight">
				{m.about_top_rated()}
			</span>
			<span className="text-muted-foreground text-xs">·</span>
			<span className="font-mono text-muted-foreground text-sm tabular-nums">
				{formatNumber(2024, { useGrouping: false })}
			</span>
		</span>
	);
}

export function About({ className, delay = 0.3, ...props }: AboutProps) {
	const { msg: contactMsg, href: contactHref, target } = useSohanEmon().contact;
	const location = useLocation();
	const currentPath = location.pathname;
	const { show: mockShow } = useClickMockStore();
	const os = isLinkActive({ targetPath: '/os', currentPath });
	const rootRef = useRef<HTMLDivElement>(null);
	const [inWindow, setInWindow] = useState(false);

	// NOTE: About mounts twice (page section + OS window sharing one
	// storage key). Only the window instance portals one level up; the
	// page instance keeps its mock inline so dismissing one can't leave
	// the other's mock behind.
	useLayoutEffect(() => {
		setInWindow(!!rootRef.current?.closest('#about--container'));
	}, []);

	const mock = <ClickMock className="absolute top-0 right-0 max-lg:hidden" />;

	return (
		<div
			ref={rootRef}
			onKeyUp={undefined}
			className={cn('container relative max-w-3xl', className)}
			{...props}
		>
			{mockShow && !os && inWindow && (
				<Portal container="#about--container">{mock}</Portal>
			)}
			{mockShow && !os && !inWindow && mock}
			<Heading
				titlePrefix={m.heading_about_prefix()}
				titleHighlight={m.heading_about_highlight()}
				description={m.about_heading_tagline()}
			/>
			<div className="relative pt-12 max-md:text-sm">
				<Motion asChild initial="bottom-xs" delay={delay + 0.3}>
					<P intent={'title'}>{m.about_title()}</P>
				</Motion>

				<Motion asChild initial="bottom-xs" delay={delay + 0.7}>
					<p className="mt-6 text-foreground/70">
						{m.about_bio_intro_prefix()}
						<strong>{m.about_degree()}</strong>
						{m.about_bio_intro_mid()}
						<strong>{formatNumber(3)}+</strong>
						{m.about_bio_intro_suffix()} {m.about_bio_body()}
						{!noContact && (
							<Link to="/readme/sohanemon" className="link ps-1">
								{m.about_read_more()}
								<SrOnly>{m.about_read_more_desc()}</SrOnly>
							</Link>
						)}
					</p>
				</Motion>

				<Motion
					initial="bottom-xs"
					delay={delay + 1}
					className="flex flex-wrap items-center gap-3 py-5"
				>
					{(env === 'fiverr' || isNonePlatform) && (
						<BadgePill variant="fiverr" />
					)}
					{(env === 'upwork' || isNonePlatform) && (
						<BadgePill variant="upwork" />
					)}
				</Motion>

				<Motion
					initial="bottom-xs"
					delay={delay + 1.3}
					className="flex flex-wrap items-center justify-between gap-4 border-border/50 border-t pt-4"
				>
					<P className="shrink-0 font-stalemate text-4xl text-primary md:text-5xl">
						SohanEmon
					</P>
					<div className="flex items-center gap-px">
						<Button
							variant="ghost"
							className="hover:translate-y-0"
							href="https://showproof.io/@sohanemon"
							target="_blank"
							icon="lucide:check"
						>
							{m.about_show_proof()}
						</Button>
						<Button noRef target={target} variant="slide" href={contactHref}>
							{contactMsg}
						</Button>
					</div>
				</Motion>
			</div>
		</div>
	);
}
